import { z } from 'zod';
import {
  normalizeEncounterTier,
  normalizeRaceId,
  normalizeOptionalBuildTemplateId,
  normalizeOptionalIdentityTemplateId,
} from '@/IsekaiRPG/modules/character/characterSemantic';

import { CharacterSceneRoleSchema } from './characterPresence';
import { BuildingTypeSchema } from '@/IsekaiRPG/modules/city/cityTypes';
import { LibraryMetaSchema, RankSchema } from '@/IsekaiRPG/shared/types';

export const CharacterSeedEncounterTierSchema = z.preprocess(
  value => normalizeEncounterTier(typeof value === 'string' ? value : undefined),
  z.enum(['杂兵', '精英', '首领']),
);
export type CharacterSeedEncounterTier = z.infer<typeof CharacterSeedEncounterTierSchema>;

export const CharacterSeedEncounterProfileSchema = z.object({
  encounterTier: CharacterSeedEncounterTierSchema,
});
export type CharacterSeedEncounterProfile = z.infer<typeof CharacterSeedEncounterProfileSchema>;

export const CharacterSeedBuildingProfileSchema = z.object({
  buildingType: BuildingTypeSchema,
  sceneRole: CharacterSceneRoleSchema,
});
export type CharacterSeedBuildingProfile = z.infer<typeof CharacterSeedBuildingProfileSchema>;

export const CharacterSeedAmbientProfileSchema = z.object({
  buildingTypes: z.array(BuildingTypeSchema).default([]),
  anchorBuildingType: BuildingTypeSchema.optional(),
  assignResidence: z.boolean().default(false),
});
export type CharacterSeedAmbientProfile = z.infer<typeof CharacterSeedAmbientProfileSchema>;

export const CharacterSeedDefinitionSeedSchema = z.object({
  raceId: z.preprocess(value => normalizeRaceId(typeof value === 'string' ? value : undefined), z.string().min(1)),
  identityTemplateId: z
    .preprocess(
      value => normalizeOptionalIdentityTemplateId(typeof value === 'string' ? value : undefined),
      z.string().optional(),
    )
    .optional(),
  buildTemplateId: z
    .preprocess(
      value => normalizeOptionalBuildTemplateId(typeof value === 'string' ? value : undefined),
      z.string().optional(),
    )
    .optional(),
  rank: RankSchema.optional(),
  nameOverride: z.string().optional(),
});
export type CharacterSeedDefinitionSeed = z.infer<typeof CharacterSeedDefinitionSeedSchema>;

export const CharacterSeedDefinitionSchema = z.object({
  id: z.string().min(1),
  worldBookId: z.string().min(1).optional(),
  seed: CharacterSeedDefinitionSeedSchema,
  libraryMeta: LibraryMetaSchema,
  encounterProfile: CharacterSeedEncounterProfileSchema.optional(),
  buildingProfile: CharacterSeedBuildingProfileSchema.optional(),
  ambientProfile: CharacterSeedAmbientProfileSchema.optional(),
});
export type CharacterSeedDefinition = z.infer<typeof CharacterSeedDefinitionSchema>;

export interface EncounterCharacterSeedDefinition extends CharacterSeedDefinition {
  encounterProfile: CharacterSeedEncounterProfile;
}

export interface BuildingResidentCharacterSeedDefinition extends CharacterSeedDefinition {
  buildingProfile: CharacterSeedBuildingProfile;
}

export interface AmbientCharacterSeedDefinition extends CharacterSeedDefinition {
  ambientProfile: CharacterSeedAmbientProfile;
}
